import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { colonnaTag, useTagDi } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import type { DiscoveryPlan } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * I piani di discovery di rete.
 *
 * L'elenco mostra **quanti passi** e **gli avvisi aperti**, non solo i nomi: un
 * piano si sceglie per lanciarlo su una rete di produzione, e «tre passi, un
 * avviso sul costo» è ciò che serve sapere prima di farlo — non dopo.
 *
 * ## Il difetto che questa pagina aveva
 *
 * Un piano si poteva creare, modificare e cancellare. **Non eseguire.** Chi ne
 * disegnava uno restava con un documento e nessun modo di usarlo: la scansione
 * andava lanciata dai Job, dove il tipo «Rete» non offriva nessun modo di
 * scegliere il piano. Le due schermate esistevano entrambe e non si toccavano.
 */
export default function DiscoveryPlans() {
  const plans = useAsync(() => api.discoveryPlans(), [])
  const [removing, setRemoving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Quale piano si sta per lanciare. Il riquadro si apre sotto la riga: chiedere
  // l'ambito in una finestra di sistema non lascerebbe modo di dire quali sono
  // gli ambiti predefiniti, che è l'informazione che serve per rispondere.
  const [launching, setLaunching] = useState<DiscoveryPlan | null>(null)
  const tag = useTagDi('discovery_plan')

  if (plans.loading) return <Spinner />
  if (plans.error) return <ErrorBanner error={plans.error} />

  const rows = plans.data ?? []

  async function remove(id: string, name: string) {
    if (!window.confirm(t('Eliminare il piano «{nome}»? Le scansioni già eseguite restano.', { nome: name }))) {
      return
    }
    setRemoving(id)
    setError(null)
    try {
      await api.deletePlan(id)
      plans.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRemoving(null)
    }
  }

  const colonne: Column<DiscoveryPlan>[] = [
    {
      key: 'name',
      header: t('Nome'),
      value: (plan) => [plan.name, plan.is_default ? t('predefinito') : '', plan.description].filter(Boolean).join(' '),
      render: (plan) => (
        <>
          <Link to={`/discovery-plans/${plan.id}`}>
            <strong>{plan.name}</strong>
          </Link>
          {plan.is_default && <span className="chip"> {t('predefinito')}</span>}
          {plan.description && <p className="text-muted">{plan.description}</p>}
        </>
      ),
    },
    // I tag vengono dal catalogo condiviso: si filtrano e si modificano sul posto.
    ...colonnaTag<DiscoveryPlan>('discovery_plan', tag, (plan) => plan.id),
    { key: 'steps', header: t('Passi'), type: 'number', value: (plan) => (plan.steps ?? []).length },
    {
      key: 'default_targets',
      header: t('Ambiti predefiniti'),
      value: (plan) => (plan.default_targets ?? []).join(', '),
      render: (plan) => <span className="text-muted">{(plan.default_targets ?? []).join(', ') || '—'}</span>,
    },
    {
      key: 'stato',
      header: t('Stato'),
      value: (plan) => ((plan.warnings ?? []).length === 0 ? t('pronto') : t('{n} avvisi', { n: plan.warnings.length })),
      // Un piano con avvisi aperti non si presenta come «pronto»: il numero
      // c'è, e chi lo lancia sa cosa sta lanciando.
      render: (plan) =>
        (plan.warnings ?? []).length === 0 ? (
          <span className="badge badge--ok">{t('pronto')}</span>
        ) : (
          <span className="badge badge--warn" title={(plan.warnings ?? []).map((w) => w.message).join('\n')}>
            {t('{n} avvisi', { n: plan.warnings.length })}
          </span>
        ),
    },
    {
      key: 'last_run_at',
      header: t('Ultima esecuzione'),
      type: 'date',
      value: (plan) => plan.last_run_at,
      render: (plan) => <span className="text-muted">{plan.last_run_at ? formatDateTime(plan.last_run_at) : t('mai')}</span>,
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (plan) => (
        <>
          {/* Un piano senza passi non si lancia: la scansione
              riuscirebbe senza guardare niente, e un esito verde è il
              risultato più difficile da mettere in dubbio. */}
          <button
            className="chip"
            disabled={(plan.steps ?? []).length === 0}
            title={
              (plan.steps ?? []).length === 0
                ? t('Il piano non ha passi: la scansione riuscirebbe senza guardare niente.')
                : undefined
            }
            onClick={() => setLaunching(launching?.id === plan.id ? null : plan)}
          >
            {t('esegui')}
          </button>{' '}
          <Link className="chip chip--muted" to={`/discovery-plans/${plan.id}`}>
            {t('modifica')}
          </Link>{' '}
          <button
            className="chip chip--muted"
            disabled={removing === plan.id}
            onClick={() => remove(plan.id, plan.name)}
          >
            {t('elimina')}
          </button>
        </>
      ),
    },
  ]

  return (
    <div className="page">
      <Card
        title={t('Piani di discovery di rete')}
        actions={
          <Link className="chip" to="/discovery-plans/nuovo">
            {t('Nuovo piano')}
          </Link>
        }
      >
        <p className="text-muted">
          {t('Configura il piano di discovery definendo metodi di scansione, credenziali e sequenza di esecuzione. Il job associa il piano al Pool di asset e avvia la discovery sui target compresi nel perimetro configurato.')}
        </p>

        {error && <ErrorBanner error={error} />}
        {notice && <div className="banner banner--ok">{notice}</div>}

        {rows.length === 0 ? (
          <EmptyState
            title={t('Nessun piano')}
            hint={t('Creandone uno si parte da una sequenza già sensata: ping, poi il nome, poi le porte, e i passi cari solo dove il servizio c’è.')}
          />
        ) : (
          <DataTable
            rows={rows}
            columns={colonne}
            rowKey={(plan) => plan.id}
            conteggio={{ nome: t('piani') }}
            // The launch panel opens under the plan's row, from its «esegui» button.
            expanded={(plan) => launching?.id === plan.id}
            expand={(plan) => (
              <RunPlan
                plan={plan}
                onCancel={() => setLaunching(null)}
                onDone={(message) => {
                  setNotice(message)
                  setLaunching(null)
                  plans.reload()
                }}
              />
            )}
          />
        )}
      </Card>
    </div>
  )
}

/**
 * Lanciare un piano già scritto.
 *
 * Si può cambiare poco, e apposta: il piano **è** la configurazione. Se
 * lanciandolo si potessero riscrivere i passi, due esecuzioni dello stesso piano
 * darebbero risultati diversi e il piano smetterebbe di essere ciò che dice di
 * essere.
 *
 * Restano l'ambito — «questo piano, ma solo su questa subnet» è la domanda
 * normale — e la sonda, perché scegliere la sonda è scegliere la **rete** da cui
 * guardare: la stessa scansione eseguita dalla sede sbagliata non fallisce,
 * restituisce un inventario vuoto e plausibile.
 */
function RunPlan({
  plan,
  onCancel,
  onDone,
}: {
  plan: DiscoveryPlan
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const [targets, setTargets] = useState('')
  const [runOn, setRunOn] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const probes = useAsync(() => api.probes(), [])
  const groups = useAsync(() => api.groups(), [])

  const scope = targets
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const effective = scope.length ? scope : (plan.default_targets ?? [])

  async function launch() {
    setError(null)
    setBusy(true)
    try {
      const job = await api.runPlan(plan.id, {
        targets: scope,
        probe_id: runOn.startsWith('probe:') ? runOn.slice(6) : null,
        group_id: runOn.startsWith('group:') ? runOn.slice(6) : null,
      })
      onDone(
        t('«{nome}»: job {job}… avviato su {ambito}.', {
          nome: plan.name,
          job: job.id.slice(0, 8),
          ambito: effective.join(', '),
        }),
      )
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="kindform">
      <ErrorBanner error={error} />

      <div className="row">
        <div className="field" style={{ flex: '2 1 300px' }}>
          <label htmlFor="run-targets">{t('Ambito')}</label>
          <input
            id="run-targets"
            type="text"
            value={targets}
            onChange={(e) => setTargets(e.target.value)}
            placeholder={(plan.default_targets ?? []).join(', ') || '192.168.1.0/24'}
          />
          <p className="field__hint">
            {(plan.default_targets ?? []).length > 0 ? (
              <>
                {t('Vuoto: si usano gli ambiti predefiniti del piano — {ambiti}.', {
                  ambiti: plan.default_targets.join(', '),
                })}
              </>
            ) : (
              <>
                {t('Il piano non ha ambiti predefiniti: senza un ambito qui la scansione non avrebbe indirizzi su cui girare.')}
              </>
            )}
          </p>
        </div>

        <div className="field">
          <label htmlFor="run-on">{t('Esegui su')}</label>
          <select id="run-on" value={runOn} onChange={(e) => setRunOn(e.target.value)}>
            <option value="">{t('Qualunque sonda idonea')}</option>
            {(groups.data ?? []).length > 0 && (
              <optgroup label={t('Gruppi')}>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={`group:${g.id}`}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label={t('Sonde')}>
              {(probes.data ?? []).map((p) => (
                <option key={p.id} value={`probe:${p.id}`}>
                  {p.name}
                  {p.status === 'online' ? '' : ` — ${t('non in linea')}`}
                </option>
              ))}
            </optgroup>
          </select>
          <p className="field__hint">
            {t("Scegliere la sonda è scegliere la rete da cui guardare: da un'altra rete la stessa scansione non fallisce, restituisce un inventario vuoto.")}
          </p>
        </div>
      </div>

      {(plan.warnings ?? []).length > 0 && (
        <div className="banner banner--warn">
          {/* Gli avvisi si rileggono **qui**, non solo nell'editor: chi lancia un
              piano scritto da qualcun altro non ha visto quella schermata. */}
          {plan.warnings.map((w) => w.message).join(' · ')}
        </div>
      )}

      <div className="toolbar">
        <button type="button" onClick={launch} disabled={busy || effective.length === 0}>
          {busy ? t('Avvio…') : t('Avvia adesso')}
        </button>
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Annulla')}
        </button>
      </div>
    </div>
  )
}
